import { Box } from '@mui/material';
import React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import { ETransactionStatus, ITransaction } from 'common/transactions.interface';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DateTime } from 'luxon';

const statusStyles = {
  PAGO: {
    label: 'Pago',
    bg: '#ECFDF3',
    color: '#0A7A3D',
    outline: undefined,
    icon: <CheckIcon sx={{ fontSize: 15, marginRight: '3px' }} />,
  },
  A_PAGAR: {
    label: 'A pagar',
    bg: '#EBEBFF',
    color: '#3B3DBD',
    outline: '#D7D8FA',
    icon: <HourglassTopIcon sx={{ fontSize: 15, marginRight: '3px' }} />,
  },
  PENDENTE: {
    label: 'Pendente',
    bg: '#FFF3E6',
    color: '#E26A00',
    outline: undefined,
    icon: <WarningAmberIcon sx={{ fontSize: 15, marginRight: '3px' }} />,
  },
  CANCELADO: {
    label: 'Cancelado',
    bg: '#F5F5F5',
    color: '#616161',
    outline: undefined,
    icon: <CloseIcon sx={{ fontSize: 15, marginRight: '3px' }} />,
  },
  ERRO_EMISSAO: {
    label: 'Erro em emissão',
    bg: '#FFF2EB', // orange background
    color: '#E26A00', // orange text (matches other orange chips)
    outline: '#EFC3A2', // outline color
    icon: <ErrorOutlineIcon sx={{ fontSize: 15, marginRight: '3px' }} />,
  },
  EM_ATRASO: {
    label: 'Em atraso',
    bg: '#FFF3E6',
    color: '#E26A00',
    outline: undefined,
    icon: <WarningAmberIcon sx={{ fontSize: 15, marginRight: '3px' }} />,
  },
};

const getReceivePageStatus = (transaction: ITransaction): keyof typeof statusStyles => {
  const { status, bankSlipDetail } = transaction;

  // Pago -> if the bank slip is paid (CONFIRMED status)
  if (status === ETransactionStatus.CONFIRMED) {
    return 'PAGO';
  }

  // Cancelado -> if the bank slip is cancelled
  if (status === ETransactionStatus.CANCELED) {
    return 'CANCELADO';
  }

  // Erro de emissão -> current error status
  if (status === ETransactionStatus.ERROR) {
    return 'ERRO_EMISSAO';
  }

  // A pagar -> if the bank slip is not processed yet (current status Enviada)
  if (status === ETransactionStatus.SENT_TO_PROVIDER) {
    return 'A_PAGAR';
  }

  // Check if overdue for remaining statuses
  if (bankSlipDetail?.payDueDate) {
    const dueDate = DateTime.fromISO(bankSlipDetail.payDueDate);
    const now = DateTime.now().startOf('day');
    const isPastDue = dueDate < now;

    // Em atraso -> If not paid AND past due
    if (isPastDue) {
      return 'EM_ATRASO';
    }
  }

  // A pagar -> if not paid and not past due
  return 'A_PAGAR';
};

const Chip = ({ status, transaction }: { status?: ETransactionStatus; transaction?: ITransaction }) => {
  let statusKey: keyof typeof statusStyles;

  if (transaction) {
    statusKey = getReceivePageStatus(transaction);
  } else {
    // Fallback to old behavior for backward compatibility
    const oldStatusMap: Record<ETransactionStatus, keyof typeof statusStyles> = {
      [ETransactionStatus.CONFIRMED]: 'PAGO',
      [ETransactionStatus.SENT_TO_PROVIDER]: 'A_PAGAR',
      [ETransactionStatus.PROCESSING]: 'PAGO',
      [ETransactionStatus.PENDING_APPROVAL]: 'PENDENTE',
      [ETransactionStatus.CANCELED]: 'CANCELADO',
      [ETransactionStatus.REJECTED_APPROVAL]: 'ERRO_EMISSAO',
      [ETransactionStatus.ERROR]: 'ERRO_EMISSAO',
      [ETransactionStatus.EXPIRED]: 'CANCELADO',
    };

    statusKey = status ? oldStatusMap[status] : 'PENDENTE';
  }

  const s = statusStyles[statusKey];

  return (
    <Box
      sx={{
        px: 1.5,
        py: 0.4,
        borderRadius: '999px',
        backgroundColor: s.bg,
        color: s.color,
        fontSize: 12,
        fontWeight: 500,
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: s.outline ? `1.5px solid ${s.outline}` : undefined,
      }}
    >
      {s.icon}
      {s.label}
    </Box>
  );
};

export default Chip;
